import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { BookOpen, Search, ArrowRight, FolderOpen, Presentation, CalendarDays } from "lucide-react";
import { YEAR_LABEL, courseYears } from "@/lib/school";
import { seminarWhen, posterUrl } from "@/lib/seminars";

const SEM_GROUPS = { seminario: [1, "Seminari"], workshop: [2, "Workshop"] };

export default function PortalCourses({ seminars = false }) {
  const [q, setQ] = useState("");

  const { data: allCourses = [], isLoading } = useQuery({
    queryKey: ["portal-courses-me"],
    queryFn: async () => (await api.get("/courses")).data,
  });
  const courses = useMemo(() => allCourses.filter((c) => !!c.is_seminar === seminars), [allCourses, seminars]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return courses;
    return courses.filter((c) =>
      (c.name || "").toLowerCase().includes(needle) ||
      (c.code || "").toLowerCase().includes(needle) ||
      (c.area_name || "").toLowerCase().includes(needle) ||
      (c.teachers || []).some((t) => (t.name || "").toLowerCase().includes(needle))
    );
  }, [courses, q]);

  // raggruppa per year_level (I, II, III…)
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((c) => {
      if (seminars) { const g = (SEM_GROUPS[c.seminar_kind] || SEM_GROUPS.seminario)[0]; (groups[g] = groups[g] || []).push(c); return; }
      const ys = courseYears(c);
      (ys.length ? ys : [0]).forEach((y) => { (groups[y] = groups[y] || []).push(c); });
    });
    if (seminars) Object.values(groups).forEach((l) => l.sort((a, b) => (b.date_from || "").localeCompare(a.date_from || "")));
    return Object.entries(groups).sort(([a], [b]) => Number(a) - Number(b));
  }, [filtered, seminars]);
  const groupLabel = (y) => (seminars ? (Number(y) === 2 ? "Workshop" : "Seminari") : y > 0 ? YEAR_LABEL[Number(y)] || `Anno ${y}` : "Senza anno");
  const noun = (n) => (seminars ? (n === 1 ? "evento" : "eventi") : n === 1 ? "corso" : "corsi");

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1200px] mx-auto" data-testid={seminars ? "portal-seminars" : "portal-courses"}>
      <PageHeader
        title={seminars ? "Seminari e workshop" : "I miei corsi"}
        subtitle={seminars ? "Locandine, date e materiali dei seminari e workshop a cui puoi accedere." : "Accedi a ciascun corso per consultare il materiale didattico caricato dai tuoi docenti."}
      />

      <div className="relative mb-5 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
        <Input
          data-testid="portal-courses-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={seminars ? "Cerca seminario, workshop, relatore…" : "Cerca corso, docente, area…"}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="text-sm text-stone-400 text-center py-8">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">
          {q ? `Nessun risultato per «${q}».` : seminars ? "Nessun seminario o workshop disponibile al momento." : "Nessun corso disponibile per il tuo anno/percorso."}
        </Card>
      ) : (
        <div className="space-y-7">
          {grouped.map(([year, items]) => (
            <section key={year}>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-stone-200"/>
                <Badge className="bg-[#1C352D] text-white text-[10px] uppercase tracking-widest rounded-full px-3 py-1">
                  {groupLabel(year)}
                </Badge>
                <span className="text-[11px] text-stone-500">{items.length} {noun(items.length)}</span>
                <div className="h-px flex-1 bg-stone-200"/>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((c) => (
                  seminars ? <SeminarCard key={c.id} course={c} /> : <CourseCard key={c.id} course={c} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function SeminarCard({ course }) {
  const { data: materials = [] } = useQuery({
    queryKey: ["portal-materials", course.id],
    queryFn: async () => (await api.get(`/courses/${course.id}/materials`)).data,
    staleTime: 60_000,
  });
  const [broken, setBroken] = useState(false);
  const hasPoster = course.poster && !broken;
  const teachers = (course.teachers || []).map((t) => t.name).join(", ");
  return (
    <Link to={`/portale/seminari/${course.id}`} data-testid={`portal-course-card-${course.id}`} className="group block">
      <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden hover:border-[#E2725B] hover:-translate-y-0.5 transition-[transform,border-color] duration-200 h-full flex flex-col">
        <div className="relative aspect-[4/5] bg-[#E2725B]/10 overflow-hidden">
          {hasPoster ? (
            <img src={posterUrl(course)} alt={`Locandina ${course.name}`} loading="lazy" onError={() => setBroken(true)}
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" data-testid={`portal-seminar-poster-${course.id}`} />
          ) : (
            <div className="w-full h-full grid place-items-center text-[#E2725B]" data-testid={`portal-seminar-noposter-${course.id}`}>
              <Presentation size={44} strokeWidth={1.3} />
            </div>
          )}
          <Badge className="absolute top-3 left-3 bg-white/90 backdrop-blur text-[#8f3e26] border border-[#E2725B]/30 text-[10px] rounded-full">
            {course.seminar_kind === "workshop" ? "WORKSHOP" : "SEMINARIO"}
          </Badge>
        </div>
        <div className="p-4 flex-1 flex flex-col">
          <div className="font-medium text-[#1C352D] leading-snug">{course.name}</div>
          <div className="text-[11px] text-stone-600 mt-1.5 flex items-center gap-1.5" data-testid={`portal-seminar-when-${course.id}`}>
            <CalendarDays size={12} className="text-stone-400 shrink-0"/> {seminarWhen(course)}
          </div>
          {teachers && <div className="text-[11px] text-stone-500 mt-1 truncate">{teachers}</div>}
          <div className="flex items-center justify-between mt-auto pt-3 border-t border-stone-100">
            <div className="flex items-center gap-1.5 text-[12px] text-stone-700">
              <FolderOpen size={13} className="text-stone-400"/><b>{materials.length}</b>
              <span className="text-stone-500">{materials.length === 1 ? "materiale" : "materiali"}</span>
            </div>
            <span className="text-[11px] text-stone-500 flex items-center gap-1 group-hover:text-[#8f3e26] transition-colors">Apri <ArrowRight size={12}/></span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function CourseCard({ course }) {
  const { data: materials = [] } = useQuery({
    queryKey: ["portal-materials", course.id],
    queryFn: async () => (await api.get(`/courses/${course.id}/materials`)).data,
    staleTime: 60_000,
  });

  const accent = course.is_seminar ? "#E2725B" : course.area_color || "#1C352D";
  const teachers = (course.teachers || []).map((t) => t.name).join(", ");
  const Icon = course.is_seminar ? Presentation : BookOpen;

  return (
    <Link
      to={`/portale/${course.is_seminar ? "seminari" : "corsi"}/${course.id}`}
      data-testid={`portal-course-card-${course.id}`}
      className="group block"
    >
      <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden hover:border-[#1C352D] transition-colors duration-150 h-full">
        <div className="h-1" style={{ background: accent }}/>
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0" style={{ background: `${accent}18`, color: accent }}>
              <Icon size={16}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#1C352D] truncate flex items-center gap-2">
                {course.name}
                {course.is_seminar && (
                  <Badge className="bg-[#E2725B]/10 text-[#8f3e26] border border-[#E2725B]/30 text-[10px] rounded-full">{course.seminar_kind === "workshop" ? "WORKSHOP" : "SEM."}</Badge>
                )}
              </div>
              <div className="text-[11px] text-stone-500 mt-0.5 truncate">
                {course.area_name || "—"}
                {course.code ? <> · <span className="mono">{course.code}</span></> : null}
              </div>
            </div>
          </div>

          {course.is_seminar && (
            <div className="text-[11px] text-stone-600 mt-3 flex items-center gap-1.5" data-testid={`portal-seminar-when-${course.id}`}>
              <CalendarDays size={12} className="text-stone-400"/> {seminarWhen(course)}
            </div>
          )}
          {teachers && (
            <div className="text-[11px] text-stone-500 mt-3 truncate">
              {teachers}
            </div>
          )}

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-100">
            <div className="flex items-center gap-1.5 text-[12px] text-stone-700">
              <FolderOpen size={13} className="text-stone-400"/>
              <b>{materials.length}</b>
              <span className="text-stone-500">{materials.length === 1 ? "materiale" : "materiali"}</span>
            </div>
            <span className="text-[11px] text-stone-500 flex items-center gap-1 group-hover:text-[#1C352D] transition-colors">
              Apri <ArrowRight size={12}/>
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
}
